import { ReactNode, useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { useIntl } from 'react-intl'
import { useQuery, useQueryClient } from 'react-query'

import sparckLogo from 'assets/images/sparck-full-colored.png'
import { sharingEnglish } from 'i18n/dashboardSharingMessages'
import {
  BoardTheme,
  storeBoardTheme,
  storedBoardTheme,
} from 'pages/sparckDashboards/components/boardTheme'
import SandboxedDashboard from 'pages/sparckDashboards/components/SandboxedDashboard'
import ThemeSwitch, {
  useBoardBackground,
} from 'pages/sparckDashboards/components/ThemeSwitch'
import { createSharedViewId } from 'utils/dashboardSharing'
import {
  readSharedDashboard,
  SharedDashboardData,
  ShareMode,
} from './sharedDashboardApi'

type BoardThemeControl = {
  value: BoardTheme
  onChange: (theme: BoardTheme) => void
}

// The viewer's theme switch, in the page's own words: the public page has only the sharing messages.
const SharedThemeSwitch = ({ value, onChange }: BoardThemeControl) => {
  const intl = useIntl()
  const say = (key: 'themeLabel' | 'themeAuto' | 'themeLight' | 'themeDark') =>
    intl.formatMessage({
      id: `dashboardSharing.${key}`,
      defaultMessage: sharingEnglish[key],
    })

  return (
    <ThemeSwitch
      value={value}
      onChange={onChange}
      labels={{
        group: say('themeLabel'),
        auto: say('themeAuto'),
        light: say('themeLight'),
        dark: say('themeDark'),
      }}
    />
  )
}

const MAIN_SX = {
  flex: '1 1 0',
  minHeight: 0,
  width: '100%',
  overflow: 'hidden',
}

// Behind a board, the page takes the --bg of the theme the board shows.
const SharedMain = ({
  theme,
  children,
}: {
  theme?: BoardTheme
  children: ReactNode
}) => {
  const background = useBoardBackground(theme ?? 'auto')

  return (
    <Box
      component='main'
      sx={theme ? { ...MAIN_SX, bgcolor: background } : MAIN_SX}
      data-cy='sharedBoardMain'
    >
      {children}
    </Box>
  )
}

// The header stays white in every theme: only the board, and the page behind it, take the viewer's.
export const SharedDashboardLayout = ({
  children,
  company,
  theme,
}: {
  children: ReactNode
  company?: SharedDashboardData['company']
  // The viewer's theme and its switch, while a board is on the page.
  theme?: BoardThemeControl
}) => (
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      height: '100%',
      '@supports (height: 100dvh)': { height: '100dvh' },
      pt: 'env(safe-area-inset-top)',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'white',
      overflow: 'hidden',
    }}
  >
    <Box
      component='header'
      sx={{
        flex: '0 0 auto',
        height: { xs: 48, sm: 56 },
        px: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderBottom: '1px solid #eee',
      }}
    >
      {company?.logo_url && (
        <Box
          component='img'
          key={company.logo_url}
          src={company.logo_url}
          alt=''
          onError={event => {
            event.currentTarget.style.display = 'none'
          }}
          referrerPolicy='no-referrer'
          sx={{
            maxWidth: 120,
            maxHeight: 32,
            minWidth: 0,
            objectFit: 'contain',
          }}
        />
      )}
      <Typography noWrap fontWeight={600} sx={{ minWidth: 0 }}>
        {company?.name}
      </Typography>
      <Box
        sx={{
          ml: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {theme && <SharedThemeSwitch {...theme} />}
        {/* On a phone the switch takes the logo's room, so it always shows
            whole, even at 320 px. */}
        <Box
          component='img'
          src={sparckLogo}
          alt='Sparck'
          sx={muiTheme => ({
            width: 92,
            flexShrink: 0,
            objectFit: 'contain',
            ...(theme
              ? {
                  display: 'none',
                  [muiTheme.breakpoints.up('sm')]: { display: 'block' },
                }
              : {}),
          })}
        />
      </Box>
    </Box>
    <SharedMain theme={theme?.value}>{children}</SharedMain>
  </Box>
)

export const SharedDashboardUnavailable = ({
  onRetry,
}: { onRetry?: () => void } = {}) => {
  const intl = useIntl()
  return (
    <Box sx={{ p: 3 }}>
      <Alert severity='error'>
        {intl.formatMessage({
          id: 'dashboardSharing.unavailable',
          defaultMessage: 'This link is unavailable or you do not have access.',
        })}
      </Alert>
      {onRetry && (
        <Button onClick={onRetry}>
          {intl.formatMessage({
            id: 'dashboardSharing.retry',
            defaultMessage: 'Try again',
          })}
        </Button>
      )}
    </Box>
  )
}

export const SharedDashboardLoading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
)

export function SharedDashboardViewer({
  uuid,
  mode,
  identity = 'public',
}: {
  uuid: string
  mode: ShareMode
  identity?: string
}) {
  const [viewId] = useState(createSharedViewId)
  const [expired, setExpired] = useState(false)
  const [stopped, setStopped] = useState(false)
  // The same choice as on the board pages of this browser.
  const [theme, setTheme] = useState<BoardTheme>(storedBoardTheme)
  const changeTheme = (next: BoardTheme) => {
    storeBoardTheme(next)
    setTheme(next)
  }
  const client = useQueryClient()
  const queryKey = ['sharedDashboard', mode, uuid, identity, viewId]
  const query = useQuery<SharedDashboardData, Error>(
    queryKey,
    ({ signal }) => readSharedDashboard(uuid, mode, viewId, signal),
    {
      retry: false,
      cacheTime: 0,
      staleTime: 0,
      keepPreviousData: false,
      enabled: !expired && !stopped,
      refetchInterval: (_data, state) =>
        state.state.status === 'error' ? false : 300000,
      refetchOnWindowFocus: true,
    },
  )

  useEffect(() => {
    if (!query.data) return undefined
    const deadline = Date.parse(query.data.share.expires_at)
    const check = () => {
      if (!Number.isFinite(deadline) || Date.now() >= deadline) setExpired(true)
    }
    check()
    const timer = window.setInterval(check, 1000)
    window.addEventListener('focus', check)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
    }
  }, [query.data])

  useEffect(
    () => () => {
      void client.cancelQueries([
        'sharedDashboard',
        mode,
        uuid,
        identity,
        viewId,
      ])
      client.removeQueries(['sharedDashboard', mode, uuid, identity, viewId])
    },
    [client, mode, uuid, identity, viewId],
  )

  const denied = expired || stopped || query.isError
  useEffect(() => {
    if (denied) {
      void client.cancelQueries([
        'sharedDashboard',
        mode,
        uuid,
        identity,
        viewId,
      ])
      client.removeQueries(['sharedDashboard', mode, uuid, identity, viewId])
    }
  }, [denied, client, mode, uuid, identity, viewId])
  const data = denied ? undefined : query.data
  return (
    <SharedDashboardLayout
      company={data?.company}
      theme={data ? { value: theme, onChange: changeTheme } : undefined}
    >
      {denied ? (
        <SharedDashboardUnavailable
          onRetry={
            query.isError && !expired && !stopped
              ? () => {
                  void query.refetch()
                }
              : undefined
          }
        />
      ) : data ? (
        <SandboxedDashboard
          html={data.publication.html}
          payload={data.payload}
          theme={theme}
          title={data.dashboard.name}
          sizing='viewport'
          onStop={() => setStopped(true)}
        />
      ) : (
        <SharedDashboardLoading />
      )}
    </SharedDashboardLayout>
  )
}
